import { z } from 'zod'

export const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string(),
  }),
})
export type ErrorResponse = z.infer<typeof errorResponseSchema>

/**
 * ── Topic model ─────────────────────────────────────────────────────────────────────
 *
 * The settled model the topic-creation wizard authors and the real API persists (sub-account-scoped
 * normalized tables; see packages/db/src/schema/app.ts and docs/topics-productionization.md). Shared
 * and typed so both the API edge and the SPA client agree on the contract.
 *
 * Hierarchy, for context: a *query* is the atom; one or many queries make a *topic*; one or many
 * topics make a *folder*. Everything here is authored at the topic level.
 */

/** The NER entity types a tag can carry. A tag is one entity keyed to a value — `org: "OpenAI"`,
 *  `person: "Jensen Huang"` — mirroring a standard named-entity-recognition dictionary. */
export const nerEntitySchema = z.enum([
  'org',
  'person',
  'location',
  'product',
  'event',
  'date',
  'misc',
])
export type NerEntity = z.infer<typeof nerEntitySchema>

/** Max length of a tag's value, exported so the tag inputs cap at the same bound the schema
 *  enforces — otherwise an over-long value fails validation only at save (same reasoning as the
 *  content-field caps below). Declared here because `tagSchema` uses it. */
export const TAG_VALUE_MAX = 120

/** A tag: one NER entity/value pair, e.g. `{ entity: 'org', value: 'OpenAI' }`. Tags live in a
 *  topic-level pool and are applied by id to the topic itself and/or to individual queries, so one
 *  tag can be shared across many queries. */
export const tagSchema = z.object({
  id: z.string(),
  entity: nerEntitySchema,
  value: z.string().min(1).max(TAG_VALUE_MAX),
})
export type Tag = z.infer<typeof tagSchema>

/** Content-field length caps, exported so the inputs that write these fields cap at the same
 *  bound the schema enforces — otherwise an over-long value fails validation only at save. */
export const NAME_MAX = 200
export const DESCRIPTION_MAX = 2000
export const REASONING_PROMPT_MAX = 4000

/** Per-query source parameters. These are authored **per query**, not per source section — one X
 *  query can restrict to verified authors while another in the same source doesn't. They're optional
 *  on the base query and present only on queries that live in the source they configure (X /
 *  podcast); everywhere else (news, LinkedIn, …) they're absent. */
export const xQueryParamsSchema = z.object({
  /** Restrict this query to posts from verified accounts. */
  verifiedOnly: z.boolean(),
  /** Only surface posts from these handles (empty = all). */
  authors: z.array(z.string()),
  /** Engagement floor: minimum (likes + reposts) a post needs to surface. 0 = no floor. Mirrors the
   *  data API's `minEngagement`, applied in-SQL there. */
  minEngagement: z.number().int().nonnegative(),
})
export type XQueryParams = z.infer<typeof xQueryParamsSchema>

/** A Podchaser show reference — the real shape prod stores in `podcastCreatorInclusions`. `id` is the
 *  Podchaser podcast id sent to the transcript search (`identifiers`); `title` is display only. This
 *  is an open catalogue (millions of shows), so shows are chosen via a Podchaser search, never a fixed
 *  list — see the note in `PodcastFields`; that search endpoint is still an eng dependency. */
export const podcastShowSchema = z.object({
  id: z.string(),
  title: z.string(),
})
export type PodcastShow = z.infer<typeof podcastShowSchema>

export const podcastQueryParamsSchema = z.object({
  /** Only surface episodes from these Podchaser shows (empty = all). Mirrors the live collector's
   *  `creatorInclusions`; the pipeline consumes the `id`s. */
  creatorInclusions: z.array(podcastShowSchema),
  /** Restrict to these Podchaser categories (empty = all). Category **slugs** from the fixed Podchaser
   *  taxonomy (see `podcast-categories.ts`), mirroring the collector's `categories`. */
  categories: z.array(z.string()),
})
export type PodcastQueryParams = z.infer<typeof podcastQueryParamsSchema>

/** Per-query broadcast (TV/radio) parameters — a market/geo scope, not part of the boolean
 *  expression. These map to the TvEyes `Market.*` Lucene filters downstream
 *  (`clipbook-airflow/jobs/tveyes-sync/broadcast_query_builder.py`), which take exactly ONE state and
 *  ONE country plus a DMA-rank range — so `state`/`country` are single values, not lists. Null means
 *  unscoped on that axis. */
export const broadcastQueryParamsSchema = z
  .object({
    /** US state code (e.g. "NY"), or null for all. Single-valued — the builder takes one. */
    state: z.string().nullable(),
    /** Country code, or null for all / US-implicit. Single-valued. */
    country: z.string().nullable(),
    /** DMA market-rank window (Nielsen 1–210). Null = unbounded on that side. */
    dmaRankMin: z.number().int().positive().nullable(),
    dmaRankMax: z.number().int().positive().nullable(),
  })
  .refine((v) => v.dmaRankMin === null || v.dmaRankMax === null || v.dmaRankMin <= v.dmaRankMax, {
    message: 'DMA rank minimum must be ≤ maximum.',
    path: ['dmaRankMax'],
  })
export type BroadcastQueryParams = z.infer<typeof broadcastQueryParamsSchema>

/** The atom: a single boolean news/search query. Queries carry no title — they're identified by
 *  their `expression` plus the tags associated with them (`tagIds`, referencing the topic-level tag
 *  pool so one tag is shared across many queries). `x` / `podcast` hold the per-query source
 *  parameters above, set only when the query lives in that source. */
export const topicQuerySchema = z.object({
  id: z.string(),
  expression: z.string(),
  tagIds: z.array(z.string()),
  x: xQueryParamsSchema.optional(),
  podcast: podcastQueryParamsSchema.optional(),
  broadcast: broadcastQueryParamsSchema.optional(),
})
export type TopicQuery = z.infer<typeof topicQuerySchema>

/** The news query section — one positive query list like every other source, plus `exclusionQueries`:
 *  full booleans used as the inverse, where a match drops the item. Both are authored with the same
 *  query editor. */
export const newsQuerySectionSchema = z.object({
  // News is a source too — on by default for every topic, but can be turned off.
  enabled: z.boolean(),
  queries: z.array(topicQuerySchema),
  exclusionQueries: z.array(topicQuerySchema),
  /**
   * Bylines this topic tracks (CLI-897): a story whose author is one of these is the topic's, with
   * no query matching it — legacy's header `authors` list, which the data API matches on the
   * hit's byline. Plain names, not booleans. Optional so a draft from a client that never shows
   * them leaves them as they are; the record always carries them.
   */
  authors: z.array(z.string().trim().min(1).max(200)).max(500).optional(),
})
export type NewsQuerySection = z.infer<typeof newsQuerySectionSchema>

/**
 * A stored topic row is a tracked byline (CLI-897) when its bucket is `author`: matched on the hit's
 * author by the data API, never a query — not an include, not an exclusion, never LinkedIn's, never
 * polled by an RTA. The one statement of that rule; the pull plan, the RTA runner and the topic
 * record all ask here.
 */
export function isAuthorRow(row: { bucket: string }): boolean {
  return row.bucket === 'author'
}

/** A topic's tracked bylines: its news author rows' names, in stored order. */
export function trackedAuthorsOf(
  rows: readonly { source: string; bucket: string; expression: string }[],
): string[] {
  return rows
    .filter((row) => isAuthorRow(row) && row.source === 'news')
    .map((row) => row.expression)
}

/** The platforms a topic can carry its own source-specific queries for. */
export const topicSourceSchema = z.enum(['x', 'linkedin', 'broadcast', 'podcast', 'newsletter'])
export type TopicSource = z.infer<typeof topicSourceSchema>

/** Every source is checkbox-gated: `enabled` (pre-selected true) reveals its query creator, and it
 *  carries its own boolean `queries`. Source parameters that used to live here (X's verified/authors,
 *  podcast's creator priority/exclusions) are now authored per query — see `xQueryParamsSchema` /
 *  `podcastQueryParamsSchema` on `topicQuerySchema` — so every source shares this same shape. */
export const sourceConfigSchema = z.object({
  enabled: z.boolean(),
  queries: z.array(topicQuerySchema),
})
export type SourceConfig = z.infer<typeof sourceConfigSchema>

/** Broadcast and podcast carry one thing the other platforms don't: `termExclusions`, plain terms
 *  that gate a clip or episode at the SNIPPET — a segment whose matched chunk mentions one is not
 *  served for this topic (the story drops only when every chunk does). It is legacy's header-level
 *  `termExclusionAtHeaderLevel`, which the portal applied to those two sources at the snippet, and
 *  the data API's topic mode takes it per topic (CLI-625). Source-level, like newsletter `outlets`,
 *  because legacy kept it per header, not per term. Optional so a draft without it is unchanged;
 *  absent reads as none. No wizard surface yet — migrated topics carry it, the app round-trips it. */
export const snippetSourceConfigSchema = sourceConfigSchema.extend({
  termExclusions: z.array(z.string()).optional(),
})
export type SnippetSourceConfig = z.infer<typeof snippetSourceConfigSchema>

/** Newsletters carry one extra thing every other source doesn't: the actual outlets to subscribe
 *  to, one email address each. Unlike X's `authors` or podcast's creator lists — per-query params
 *  that filter a feed already received — a newsletter must be *subscribed to* before any term can
 *  match, so `outlets` is source-level (subscribe once) and the term queries filter across whatever
 *  those outlets send. Stored as plain emails; a display name can come later. */
export const newsletterConfigSchema = sourceConfigSchema.extend({
  outlets: z.array(z.email()),
})
export type NewsletterConfig = z.infer<typeof newsletterConfigSchema>

/** Source-specific configuration, one entry per platform. Uniform except newsletter, which adds its
 *  subscription outlets (see `newsletterConfigSchema`). */
export const sourceQueriesSchema = z.object({
  x: sourceConfigSchema,
  linkedin: sourceConfigSchema,
  broadcast: snippetSourceConfigSchema,
  podcast: snippetSourceConfigSchema,
  newsletter: newsletterConfigSchema,
})
export type SourceQueries = z.infer<typeof sourceQueriesSchema>

/** The account-level shared resource library: tags defined once and reused across every topic.
 *  Topics reference these by id (query `tagIds`, topic `topicTagIds`); the definitions live here,
 *  not on any single topic. */
export const resourceLibrarySchema = z.object({
  tags: z.array(tagSchema),
})
export type ResourceLibrary = z.infer<typeof resourceLibrarySchema>

/** Tags are NOT stored on the topic — they live in the shared library and are referenced by id.
 *  `topicTagIds` are the library tags applied to the whole topic; per-query application lives on
 *  each query's `tagIds`. */

/** The reasoning filter: an optional LLM relevance pass layered on top of the boolean queries. Its
 *  `mode` decides what happens to an item the model judges off-topic: `advise` keeps it but flags it
 *  (the default — nothing is silently dropped); `filter` removes it from results. */
export const reasoningModeSchema = z.enum(['advise', 'filter'])
export type ReasoningMode = z.infer<typeof reasoningModeSchema>

export const reasoningFilterSchema = z.object({
  enabled: z.boolean(),
  prompt: z.string().max(REASONING_PROMPT_MAX),
  model: z.string(),
  mode: reasoningModeSchema,
})
export type ReasoningFilter = z.infer<typeof reasoningFilterSchema>

/** Whether a topic is running. `draft` is saved-but-paused (nothing fetches); `live` is activated
 *  from the wizard's Review step. Kept as an enum, not a bare boolean, so a `paused` state can slot
 *  in later without a data migration. */
export const topicStatusSchema = z.enum(['draft', 'live'])
export type TopicStatus = z.infer<typeof topicStatusSchema>

/**
 * The products a topic is used for (CLI-744, product/eng 2026-09-29). Any topic can feed any product
 * — a report header, the Library/Analytics stream, an RTA, the API — and nothing refuses one. The set
 * is a label that follows use: attaching a topic to a product adds that product server-side (or, for
 * the stream, when its Configure page attaches it), and a person can add or remove one in the wizard.
 * It is never cleared automatically when use stops, so it reads as "meant for", not a live count.
 * Badges on the topic list and the ordering/filter in each product's picker read it; no gate does.
 *
 * `api` marks a topic served through the Clipbook API and MCP (clipbook-api). It has one reader
 * outside this repo: clipbook-ingest matches every live topic carrying it, as well as the topics of
 * Headline-delivered reports, so an API-only topic (in no report) needs it to be matched. Which
 * topics a customer's keys may read is the API endpoint's own list, never this label.
 */
export const TOPIC_PRODUCTS = ['reports', 'analytics', 'rta', 'api'] as const
export const topicProductSchema = z.enum(TOPIC_PRODUCTS)
export type TopicProduct = z.infer<typeof topicProductSchema>
export const TOPIC_PRODUCT_LABELS: Record<TopicProduct, string> = {
  reports: 'Reports',
  analytics: 'Analytics',
  rta: 'Real-time alerts',
  api: 'API',
}

/** Record that products now use a topic. Additive — dropping one is an edit in the wizard. */
export const useTopicSchema = z.object({ products: z.array(topicProductSchema).min(1) })
export type UseTopic = z.infer<typeof useTopicSchema>

export const topicDraftSchema = z.object({
  name: z.string().min(1).max(NAME_MAX),
  /** Canonical order, de-duplicated — see TOPIC_PRODUCTS. Optional on create. */
  products: z
    .array(topicProductSchema)
    .default([])
    .transform((products) => TOPIC_PRODUCTS.filter((product) => products.includes(product))),
  description: z.string().max(DESCRIPTION_MAX),
  status: topicStatusSchema,
  topicTagIds: z.array(z.string()),
  news: newsQuerySectionSchema,
  /**
   * The topic's "hide stories mentioning…" list — legacy's term exclusions (the subscription's
   * `term_exclusions` and the header's `termExclusionAtHeaderLevel`): plain words, not booleans. A
   * story whose headline, summary or snippet carries one is left out of this topic's news,
   * newsletters and broadcast clips; the data API applies the rule after the pull, so the slot is
   * not refilled. The other box, `news.exclusionQueries`, is full booleans matched on the whole
   * story. Optional so a client that never shows the box saves the record as it read it.
   */
  termExclusions: z.array(z.string().max(200)).max(1000).optional(),
  sources: sourceQueriesSchema,
  reasoningFilter: reasoningFilterSchema,
})
export type TopicDraft = z.infer<typeof topicDraftSchema>

/** A folder: an account-level container that houses topics. Purely organizational — it holds
 *  nothing but a name and description; all authoring still happens at the topic level. A topic
 *  points at its folder by id (`TopicRecord.folderId`, null when ungrouped), so a folder is not a
 *  wizard step — it's created and named on its own. */
export const folderSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(NAME_MAX),
  description: z.string().max(DESCRIPTION_MAX),
  createdAt: z.iso.datetime(),
})
export type Folder = z.infer<typeof folderSchema>

/** The name/description a user supplies when creating or renaming a folder. */
export const folderDraftSchema = folderSchema.pick({ name: true, description: true })
export type FolderDraft = z.infer<typeof folderDraftSchema>

/** A saved topic in the client store — the draft plus the fields the store assigns. `folderId` is
 *  managed outside the wizard (moved from the list), so it lives on the record, not the draft. */
export const topicRecordSchema = topicDraftSchema.extend({
  id: z.string(),
  createdAt: z.iso.datetime(),
  folderId: z.string().nullable(),
})
export type TopicRecord = z.infer<typeof topicRecordSchema>

// ── API request/response contracts ────────────────────────────────────────────────
// Topics are addressed under a sub-account (`/api/topics/:subaccountId/...`), so `subaccountId` rides
// in the path, not these bodies. A topic response is the full record; the client already knows its
// sub-account from the route.

export const topicsResponseSchema = z.object({ topics: z.array(topicRecordSchema) })
export type TopicsResponse = z.infer<typeof topicsResponseSchema>

/** Move a topic into a folder (or out of one with null). */
export const moveTopicSchema = z.object({ folderId: z.string().nullable() })
export type MoveTopic = z.infer<typeof moveTopicSchema>

/**
 * Copy a topic into other sub-accounts of the SAME parent account. One-time deep copies, not links:
 * the receiving sub-account owns each copy and may edit it, and the original stays put. Targets are
 * validated to share the origin's account server-side.
 */
export const copyTopicSchema = z.object({
  targetSubaccountIds: z.array(z.uuid()).min(1).max(100),
})
export type CopyTopic = z.infer<typeof copyTopicSchema>

export const copyTopicResponseSchema = z.object({ copied: z.number().int().nonnegative() })
export type CopyTopicResponse = z.infer<typeof copyTopicResponseSchema>

// ── Topics Library (staff, cross-client) ──────────────────────────────────────────
// The internal Topics Library lists EVERY topic across every client org-wide and copies one from
// one client to another (across accounts, staff-only). Topics have no ownerless "library" scope —
// each is owned by a sub-account — so these are a light cross-client VIEW plus a copy, not an
// ownership model. The list is a summary (never the full record): assembling every topic's
// queries/sources for a whole org would be far too heavy.

/** One row of the Topics Library: a topic's identity + live/draft + positive-query count, plus the
 *  sub-account and account that own it. `isPrimary` marks an account's own space vs a client. */
export const topicLibraryRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: topicStatusSchema,
  products: z.array(topicProductSchema),
  queryCount: z.number().int().nonnegative(),
  subaccountId: z.string(),
  subaccountName: z.string(),
  accountId: z.string(),
  accountName: z.string(),
  isPrimary: z.boolean(),
})
export type TopicLibraryRow = z.infer<typeof topicLibraryRowSchema>

export const listTopicLibraryResponseSchema = z.object({
  topics: z.array(topicLibraryRowSchema),
})
export type ListTopicLibraryResponse = z.infer<typeof listTopicLibraryResponseSchema>

/** A copy target for the library: every sub-account in the org (client or an account's own space),
 *  carrying its account so the picker can label it "Account · Client". */
export const topicLibraryClientSchema = z.object({
  subaccountId: z.string(),
  subaccountName: z.string(),
  accountId: z.string(),
  accountName: z.string(),
  isPrimary: z.boolean(),
})
export type TopicLibraryClient = z.infer<typeof topicLibraryClientSchema>

export const topicLibraryClientsResponseSchema = z.object({
  clients: z.array(topicLibraryClientSchema),
})
export type TopicLibraryClientsResponse = z.infer<typeof topicLibraryClientsResponseSchema>

export const foldersResponseSchema = z.object({ folders: z.array(folderSchema) })
export type FoldersResponse = z.infer<typeof foldersResponseSchema>

/** Create a tag in the sub-account's shared pool. */
export const createTagSchema = z.object({
  entity: nerEntitySchema,
  value: z.string().min(1).max(TAG_VALUE_MAX),
})
export type CreateTag = z.infer<typeof createTagSchema>

/** Edit a tag in place — rename it, move it to another entity type, or both. The tag keeps its id,
 *  so every topic/query mapping follows the edit; that's the point of editing over delete+create. */
export const updateTagSchema = z
  .object({
    entity: nerEntitySchema.optional(),
    value: z.string().min(1).max(TAG_VALUE_MAX).optional(),
  })
  .refine((body) => body.entity !== undefined || body.value !== undefined, {
    message: 'Nothing to update.',
  })
export type UpdateTag = z.infer<typeof updateTagSchema>

export const tagsResponseSchema = z.object({ tags: z.array(tagSchema) })
export type TagsResponse = z.infer<typeof tagsResponseSchema>

/** How many topics and queries reference a tag — the delete guard's counts (see §5.1 of the doc). */
export const tagUsageResponseSchema = z.object({
  topicCount: z.number().int().nonnegative(),
  queryCount: z.number().int().nonnegative(),
})
export type TagUsageResponse = z.infer<typeof tagUsageResponseSchema>
